import { Offscreen, Toast } from '@deque/cauldron-react';
import React from 'react';
import type {
  AnnouncementSeverity,
  PublicAnnouncement
} from '~/common/api-client';
import { useAuthContext } from '~/common/contexts/auth';
import useWebStorage, { WebStorageType } from '~/common/hooks/useWebStorage';
import useActiveAnnouncements from '../../hooks/useActiveAnnouncements';
import styles from './Announcements.css';
import { severityToToastType } from './announcementsDisplay';
import AnnouncementToastContent, {
  useSeverityPrefixes
} from './AnnouncementToastContent';

const dismissalKey = (userId?: string): string =>
  userId ? `announcements_dismissed_${userId}` : 'announcements_dismissed';

// Stable initial value: useWebStorage re-syncs whenever the initial's identity
// changes, so an inline `[]` here would loop the component forever.
const NO_DISMISSED_IDS: string[] = [];

const SEVERITY_RANK: Record<AnnouncementSeverity, number> = {
  critical: 0,
  warning: 1,
  informational: 2
};

/**
 * The single announcement to surface: most severe first, newest start date as the
 * tie-break. The rest stay queued and appear one at a time as each is dismissed.
 */
export const pickAnnouncementToShow = (
  announcements: PublicAnnouncement[],
  dismissedIds: readonly string[]
): PublicAnnouncement | null => {
  const dismissed = new Set(dismissedIds);
  const candidates = announcements
    .filter(announcement => !dismissed.has(announcement.id))
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  return candidates[0] ?? null;
};

interface AnnouncementToastsProps {
  productSlug: string;
}

/**
 * Fetches active announcements for `productSlug` from the public endpoint and surfaces
 * them one at a time as a dismissible banner toast (see `pickAnnouncementToShow` for the
 * ordering). Dismissals are tracked per-user in local storage — pruned to the ids still
 * active so the entry never grows unbounded — and never shown again.
 *
 * Screen reader announcement happens via the offscreen polite live region, which exists
 * from first render (before the fetch resolves) so arrivals are read out reliably and
 * without yanking focus. The visible Toast therefore opts out of cauldron's default
 * focus-steal (`focus={false}`) and of the assertive `role="alert"` it would otherwise
 * get (`role={undefined}`). It is rendered as a direct sibling — not wrapped — because
 * MainLayout's banner-slot CSS targets immediate `.Toast` children.
 */
const AnnouncementToasts: React.FC<AnnouncementToastsProps> = ({
  productSlug
}) => {
  const { user } = useAuthContext();
  const { announcements } = useActiveAnnouncements(productSlug);
  const severityPrefixes = useSeverityPrefixes();
  const [dismissedIds, setDismissedIds] = useWebStorage<string[]>(
    WebStorageType.Local,
    dismissalKey(user?.id),
    NO_DISMISSED_IDS
  );

  const announcement = pickAnnouncementToShow(announcements, dismissedIds);

  const onDismiss = (id: string) => {
    const activeIds = new Set(announcements.map(a => a.id));
    setDismissedIds([
      ...dismissedIds.filter(dismissedId => activeIds.has(dismissedId)),
      id
    ]);
  };

  return (
    <>
      <Offscreen role="status">
        {announcement &&
          `${severityPrefixes[announcement.severity]} ${
            announcement.description
          }`}
      </Offscreen>
      {announcement && (
        <Toast
          key={announcement.id}
          className={styles.bannerToast}
          type={severityToToastType(announcement.severity)}
          show
          focus={false}
          role={undefined}
          dismissible
          onDismiss={() => onDismiss(announcement.id)}
        >
          <AnnouncementToastContent
            severity={announcement.severity}
            description={announcement.description}
            link={announcement.link}
            linkText={announcement.linkText}
          />
        </Toast>
      )}
    </>
  );
};

export default AnnouncementToasts;
